import { useState } from 'react';
import Select from 'react-select';
import { useLoading } from '../../../components/globalloaders/LoadingContext';
import { toast } from 'react-toastify';
import { Bars3Icon, UserIcon } from "@heroicons/react/24/outline";
import { categoryOptions, entryStatusOptions, entryTypeOptions } from '../../../constants/options';
import { encryptPayload, capitalizeWords, toDateTimeIso } from '../../../constants/helpers';
import ConfirmDialog from '../../../components/dialogbox/ConfirmDialog';
import api from '../../../utils/auth/api';
import EntitySelector from '../components/entry/EntitySelector';

const Entry = ({ setIsMobileOpen }) => {

  const { isLoading, setIsLoading } = useLoading();

  const [dialog, setDialog] = useState({
    open: false,
    type: "info",
    title: "",
    message: "",
    confirmText: "",
    action: () => { },
  });

  // Add DialogBox Code & Store API
  const initialFormState = {
    employeeRef: '',
    clientAddress: '',
    category: '',
    date: '',
    type: 'new',
    status: 'pending',
  };
  const [formData, setFormData] = useState(initialFormState);
  const handleSelectChange = (selectedOption, { name }) => {
    setFormData({
      ...formData,
      [name]: selectedOption ? selectedOption.value : "",
    });
  };
  const onChange = (e) => {
    const { name, value } = e.target;
    const autoCapFields = ["clientAddress"];
    setFormData({
      ...formData,
      [name]: autoCapFields.includes(name) ? capitalizeWords(value) : value,
    });
  };
  const handleSubmit = async () => {

    if (isLoading) return;

    // --- Required field checks ---
    if (!formData.category) {
      toast.warning("Category is required.");
      return;
    }
    if (!formData.date) {
      toast.warning("Date is required.");
      return;
    }
    if (!formData.type) {
      toast.warning("Entry Type is required.");
      return;
    }
    if (!formData.status) {
      toast.warning("Entry Status is required.");
      return;
    }
    if (!formData.employeeRef) {
      toast.warning("Employee is required.");
      return;
    }
    if (!formData.clientAddress) {
      toast.warning("Client Address is required.");
      return;
    }

    const payload = {
      ...formData,
      date: toDateTimeIso(formData.date),
    };
    const encryptedData = encryptPayload(payload);

    setIsLoading(true);
    try {
      const response = await api.post(`${process.env.REACT_APP_URI}/entry/rider-create`, { data: encryptedData });
      if (response.data.success) {
        toast.success(response.data.message);
        setFormData(initialFormState);
      }
      else {
        toast.error(response.data.message);
      }
    }
    catch (error) {
      console.log("Entry Creation failed:", error);
      if (error.response && error.response.data) {
        const data = error.response.data;
        if (data.errors && Array.isArray(data.errors) && data.errors.length > 0) {
          data.errors.forEach(err => toast.warning(err));
        } else {
          toast.error(data.message);
        }
      } else {
        toast.error("Unable to Create Entry Network Error, please try again!");
      }
    }
    finally {
      setIsLoading(false);
      return;
    }
  };

  // Selector State handler
  const [selector, setSelector] = useState({
    open: false,
    type: null, // "employee" | "client" | "agreement"
    mode: "",
  });
  const openSelector = (type, mode) => {
    setSelector({ open: true, type, mode });
  };
  const handleEntitySelect = (item) => {
    const setter = setFormData;
    setter(prev => {
      if (selector.type === "employee") {
        return { ...prev, employeeRef: item._id, employeeObj: item };
      }
      return prev;
    });
  };

  return (
    <>
      <ConfirmDialog
        isOpen={dialog.open}
        onClose={() => setDialog((d) => ({ ...d, open: false }))}
        onConfirm={dialog.action}
        title={dialog.title}
        message={dialog.message}
        confirmText={dialog.confirmText}
        type={dialog.type}
      />
      <EntitySelector
        open={selector.open}
        onClose={() => setSelector({ open: false, type: null })}
        label={selector.type}
        apiPath={"/employee/selector-search"}
        displayFields={["name", "father_husband_name", "phone"]}
        onSelect={handleEntitySelect}
      />
      <div className="w-full">
        {/* Heading */}
        <div className="w-full mb-5 flex flex-wrap items-center justify-between gap-2 sm:gap-4">
          <div className="flex items-center space-x-2 sm:space-x-3">
            <button
              className="md:hidden p-2 bg-white shadow rounded-lg"
              onClick={() => setIsMobileOpen(true)}
            >
              <Bars3Icon className="h-5 w-5 sm:h-6 sm:w-6 text-gray-800" />
            </button>
            <div className="flex flex-wrap items-center">
              <h1 className="text-2xl sm:text-3xl font-extrabold tracking-wide text-blue-600">
                En
              </h1>
              <span className="text-2xl sm:text-3xl font-extrabold tracking-wide text-gray-800">
                try
              </span>
            </div>
          </div>
        </div>

        <hr className="border-t border-gray-200 my-4" />

        {/* Data Creation Code */}
        <div className="w-full bg-white rounded-2xl border border-gray-200 p-4 space-y-4">
          <div className='flex flex-col items-center'>
            <h2 className="text-xl sm:text-2xl font-semibold text-gray-800">
              Create Entry
            </h2>
            <p className="text-sm text-gray-500">
              Add new entry details below.
            </p>
          </div>
          <div className="grid grid-cols-1 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Category</label>
              <Select
                name="category"
                options={categoryOptions}
                placeholder="Select Category"
                value={formData.category ? categoryOptions.find(option => option.value === formData.category) : null}
                onChange={(selectedOption) =>
                  handleSelectChange(selectedOption, { name: 'category' })
                }
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Date</label>
              <input className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-400 outline-none"
                type="datetime-local"
                name='date'
                value={formData.date}
                onChange={onChange}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Type</label>
              <Select
                name="type"
                options={entryTypeOptions}
                placeholder="Select Type"
                value={formData.type ? entryTypeOptions.find(option => option.value === formData.type) : null}
                onChange={(selectedOption) =>
                  handleSelectChange(selectedOption, { name: 'type' })
                }
                isDisabled={true}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Status</label>
              <Select
                name="status"
                options={entryStatusOptions}
                placeholder="Select Status"
                value={formData.status ? entryStatusOptions.find(option => option.value === formData.status) : null}
                onChange={(selectedOption) =>
                  handleSelectChange(selectedOption, { name: 'status' })
                }
                isDisabled={true}
              />
            </div>

            <button
              type="button"
              onClick={() => openSelector("employee", "create")}
              className="w-full flex items-center justify-between px-4 py-3 border rounded-xl bg-white hover:bg-gray-50 transition shadow-sm"
            >
              <div className="flex items-center gap-3 min-w-0">
                <UserIcon className="h-5 w-5 text-blue-600" />
                <div className="flex flex-col text-left min-w-0">
                  <span className="text-xs text-gray-500">Employee</span>
                  <span className="text-sm font-medium text-gray-800 truncate">
                    {formData.employeeObj?.name || "Select Employee"}
                  </span>
                </div>
              </div>
              <span className="text-xs text-gray-400">
                {formData.employeeRef ? "Change" : ""}
              </span>
            </button>

            <div>
              <label className="block text-sm font-medium text-gray-700">Client Address</label>
              <input className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-400 outline-none"
                placeholder="H#01, ST#01, ABC..."
                type="text"
                name='clientAddress'
                value={formData.clientAddress}
                onChange={onChange}
              />
            </div>
          </div>
          <div className="border-t pt-3 flex justify-between">
            <button
              type='button'
              onClick={() => setFormData(initialFormState)}
              className="px-3 sm:px-4 py-2 rounded-lg text-sm sm:text-base font-medium text-gray-600 bg-gray-200 hover:bg-gray-300 transition"
            >
              Reset
            </button>
            <button
              type="button"
              onClick={() => {
                setDialog({
                  open: true,
                  type: "warning",
                  title: "Confirm Entry Creation",
                  message: "Please confirm all details are correct. This action will create the entry record.",
                  confirmText: "Confirm",
                  action: () => handleSubmit(),
                })
              }}
              className="px-3 sm:px-4 py-2 rounded-lg text-sm sm:text-base bg-blue-600 text-white hover:bg-blue-700 transition"
            >
              Create
            </button>
          </div>
        </div>
      </div>
    </>
  )
}

export default Entry;